import jwtDecode from 'jwt-decode';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { SnIntegrationsAPI } from '@lib/api/services';
import { snFadeInDown } from '@lib/common/animations';

import { SnSessionService } from '../../services';

@Component({
  selector: 'sn-ecom-association',
  templateUrl: './ecom-association.component.html',
  styleUrls: ['./ecom-association.component.scss'],
  animations: [snFadeInDown],
})
export class EcomAssociationComponent implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private _popup: Window | null = null;
  public isLoading: boolean = false;

  constructor(
    private readonly _dialogRef: MatDialogRef<EcomAssociationComponent>,
    private readonly _httpClient: HttpClient,
    private readonly _integrationsAPI: SnIntegrationsAPI,
    private readonly _sessionService: SnSessionService,
  ) {}

  public ngOnInit(): void {
    window.addEventListener('message', this._handlePopupMessage.bind(this));
  }

  public ngOnDestroy(): void {
    window.removeEventListener('message', this._handlePopupMessage.bind(this));

    if (this._popup && !this._popup.closed) {
      this._popup.close();
    }

    this._destroy$.next();
    this._destroy$.complete();
  }

  public connectToEcom(): void {
    // Follow FsAxonService
    let redirectURI;
    if (window.location.hostname === 'localhost') {
      redirectURI = 'http://localhost:8000/oauth/axonrespond';
    } else if (window.location.hostname.includes('stage.fususone')) {
      redirectURI = 'https://stage.fususone.com/oauth/axonrespond';
    }

    this._httpClient
      .get<{ url: string }>('/api/integrations/axon/connect/oauth/authorize/', {
        params: {
          environment: 'dashboard',
          redirect_uri: redirectURI,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: { url: string }) => {
          this._openEcomOAuthPopup(response.url);
        },
        error: (error) => {
          console.error('[Ecom Association] Failed to get OAuth URL:', error);
        },
      });
  }

  public logOut(): void {
    this._dialogRef.close();
    this._sessionService.logout({
      reason: 'ecomAssociation:declined',
    });
  }

  private _openEcomOAuthPopup(oauthUrl: string): void {
    // Follow FsAxonService
    this._popup = window.open(oauthUrl, null, 'popup=true,width=600,height=700');

    if (!this._popup || this._popup.closed) {
      console.warn('[Ecom Association] Popup was blocked or failed to open');
      return;
    }
  }

  private _handlePopupMessage(event: MessageEvent): void {
    // Only handle messages from our own popup
    if (!this._popup || event.source !== this._popup) {
      return;
    }

    // Verify origin for security
    if (event.origin !== window.location.origin) {
      console.error('[Ecom Association] Popup message from unauthorized origin:', event.origin);
      return;
    }

    if (event.data?.message === 'tokenUpdated' && event.data?.token) {
      this._associateUserWithToken(event.data.token);
    } else if (['disclaimerDeclined', 'insufficientScope'].includes(event.data?.message)) {
      console.warn('[Ecom Association] User declined or insufficient scope');
      this._dialogRef.close(false);
    }
  }

  private _associateUserWithToken(token: any): void {
    // Extract user ID from the idToken (JWT) received from the popup
    let ecomUserId: string | null = null;

    if (token?.idToken) {
      try {
        const decodedToken = jwtDecode(token.idToken);
        ecomUserId = decodedToken.sub;
      } catch (error) {
        console.error('[Ecom Association] Failed to decode idToken:', error);
      }
    }

    if (!ecomUserId) {
      console.error('[Ecom Association] No user ID found in token:', token);
      return;
    }

    this._integrationsAPI
      .associateUser(ecomUserId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this._dialogRef.close(true);
        },
        error: (error) => {
          console.error('[Ecom Association] Association failed:', error);
          // Toast already shown by integrations service
          // Don't close dialog - let user try again or logout
        },
      });
  }
}
